//This file and its related models should probably not be in this lib
//They came from the Profile libs, and deal exclusively with concerns in those libs
//TODO: move this into a separate profile-models lib instead of the generic shared/utils
import { AlertDto } from '@sd-portal/shared/models';
import * as moment from 'moment';

import { ACTypeRating } from './ac-type-rating.model';
import { AlertTypeEnum } from './alerts.model';
import { ProfileBusinessPerson } from './business-person.model';
import { Address, ContactInfo, EmailAddress, PhoneNumber } from './contact-info.model';
import { CrewMember } from './crew-member.model';
import { EmergencyContact } from './emergency-contact.model';
import { ExecutiveAssociate } from './executive-associate.model';
import { Passenger } from './passenger.model';
import { Person, PersonSummary } from './person.model';
import {
  CateringPreferences,
  GroundTransportationPreference,
  HotelPreference,
  AirlinePreference,
  Preferences
} from './preferences.model';
import { StaffInfo } from './staff-info.model';

export function generatePhoneNumber(
  number: string,
  countryId: number,
  params?: Partial<PhoneNumber>
): PhoneNumber {
  const defaults: PhoneNumber = {
    phoneNumberId: 0,
    isPrimary: true,
    number,
    countryId,
    phoneNumberTypeId: 3,
    phoneCode: '',
    countryCodeOverride: ''
  };

  return mergeModels(defaults, params);
}

export function generateEmailAddress(
  address: string,
  params?: Partial<EmailAddress>
): EmailAddress {
  const defaults: EmailAddress = {
    emailAddressId: 0,
    isPrimary: true,
    address,
    emailAddressTypeId: 2
  };

  return mergeModels(defaults, params);
}

export function generateContactInfo(params?: Partial<ContactInfo>): ContactInfo {
  const defaults: ContactInfo = {
    contactInfoId: 0,
    phoneNumbers: [],
    emailAddresses: [],
    addresses: []
  };

  return mergeModels(defaults, params);
}

export function generateBusinessPerson(
  params?: Partial<ProfileBusinessPerson>
): ProfileBusinessPerson {
  const defaults: ProfileBusinessPerson = {
    businessPersonId: 0,
    departmentId: null,
    primaryCompanyAddressId: null,
    hrTitle: null,
    employeeId: null,
    initials: null,
    canpassCertificateNumber: null,
    certificateNumber: null
  } as any as ProfileBusinessPerson; // eslint-disable-line @typescript-eslint/no-explicit-any

  return mergeModels(defaults, params);
}

export function generateCrewMember(params?: Partial<CrewMember>): CrewMember {
  const defaults: CrewMember = {
    crewMemberId: 0,
    typeRatings: [],
    pilotLicense: '',
    pilotLicenseCountryId: null,
    ifrTracked: false,
    evsTracked: false
  };

  return mergeModels(defaults, params);
}

export function generatePassenger(params?: Partial<Passenger>): Passenger {
  const defaults: Passenger = {
    passengerId: 0,
    executiveAssociates: [],
    reserved: false,
    taxInfo: []
  };
  return mergeModels(defaults, params);
}

export function generatePerson(params?: Partial<Person>): Person {
  const defaults: Person = {
    personId: 0,
    active: true,

    prefix: null,
    firstName: '',
    middleName: null,
    lastName: '',
    suffix: null,
    dateOfBirth: null,
    cityOfBirth: null,
    stateProvinceOfBirth: null,
    citizenshipCountryId: null,
    weight: null,
    weightUnitId: null,
    gender: null,
    businessPerson: generateBusinessPerson(),
    contactInfo: generateContactInfo(),
    staffInfo: new StaffInfo(),
    passenger: new Passenger(),
    emergencyContacts: [],
    preferences: generatePreferences(),
    alerts: [],
    personStaffGroups: [],
    personChecklistRoleTypes: [],
    crewMember: generateCrewMember(),
    notes: null,
    tags: new Set(),
    preferredFirstName: null,
    preferredLastName: null,
    modifiedDate: null
  };

  return mergeModels(defaults, params);
}

export function generatePersonSummary(params?: Partial<PersonSummary>): PersonSummary {
  const defaults: PersonSummary = {
    active: true,
    employeeId: null,
    firstName: '',
    hrTitle: null,
    id: 0,
    businessPersonId: 0,
    lastName: '',
    middleName: null,
    prefix: null,
    primaryEmailAddress: null,
    primaryPhoneCode: null,
    primaryPhoneNumber: null,
    suffix: null,
    tags: new Set(),
    notes: null,
    preferredFirstName: null,
    preferredLastName: null,
    modifiedDate: null
  };
  return mergeModels(defaults, params);
}

export function generateEmergencyContact(params?: Partial<EmergencyContact>): EmergencyContact {
  const defaults: EmergencyContact = {
    emergencyContactId: 0,
    modifiedDate: moment(),
    contactName: '',
    isPrimary: false,
    phoneNumbers: [],
    relationship: null
  };

  return mergeModels(defaults, params);
}

export function generateAddress(params?: Partial<Address>): Address {
  const defaults: Address = {
    addressId: 0,
    addressTypeId: 2,
    city: null,
    countryId: null,
    isPrimary: false,
    postalCode: null,
    stateProvince: null,
    streetAddress1: null,
    streetAddress2: null,
    isWhileInUsa: false
  };

  return mergeModels(defaults, params);
}

export function generateACTypeRating(params?: Partial<ACTypeRating>): ACTypeRating {
  const defaults: ACTypeRating = {
    crewMemberTypeRatingId: 0,
    aircraftTypeRatingId: 0,
    crewMemberTypeId: 0,
    acTypeRatingId: 0,
    active: true,
    acTypeId: 0,
    positionId: 0,
    crewMemberTypeRatingCurrencyTypes: []
  };

  return mergeModels(defaults, params);
}

export function generatePreferences(params?: Partial<Preferences>): Preferences {
  const defaults: Preferences = {
    catering: generateCateringPreferences(),
    groundTransportations: [],
    hotels: [],
    airlines: []
  };
  return mergeModels(defaults, params);
}

export function generateCateringPreferences(
  params?: Partial<CateringPreferences>
): CateringPreferences {
  const defaults: CateringPreferences = {
    preferenceIds: {},
    allergies: null,
    beverages: null,
    breakfast: null,
    dietaryPreferences: null,
    dinner: null,
    lunch: null,
    snacks: null
  };
  return mergeModels(defaults, params);
}

export function generateGroundTransportationPreference(
  params?: Partial<GroundTransportationPreference>
): GroundTransportationPreference {
  const defaults: GroundTransportationPreference = {
    groundTransportationId: 0,
    loyaltyNumber: null,
    provider: null,
    isPrimary: false
  };
  return mergeModels(defaults, params);
}

export function generateHotelPreference(params?: Partial<HotelPreference>): HotelPreference {
  const defaults: HotelPreference = {
    hotelId: 0,
    hotelName: null,
    loyaltyNumber: null,
    isPrimary: false
  };
  return mergeModels(defaults, params);
}

export function generateAirlinePreference(params?: Partial<AirlinePreference>): AirlinePreference {
  const defaults: AirlinePreference = {
    airlineId: 0,
    airlineName: null,
    frequentFlyerNumber: null,
    isPrimary: false
  };
  return mergeModels(defaults, params);
}

export function generateAlert(params?: Partial<AlertDto>): AlertDto {
  const defaults: AlertDto = {
    alertId: 0,
    details: null,
    alertTypeId: AlertTypeEnum.Person,
    parentId: 0,
    alertStartDate: null,
    alertStartTime: '',
    alertEndDate: null,
    alertEndTime: '',
    everyDay: true,
    allDay: true
  };
  return mergeModels(defaults, params);
}

export function generateExecutiveAssociate(
  params?: Partial<ExecutiveAssociate>
): ExecutiveAssociate {
  const defaults: ExecutiveAssociate = {
    executiveAssociateId: 0,
    emailAddresses: [],
    isPrimary: false,
    name: '',
    phoneNumbers: []
  };
  return mergeModels(defaults, params);
}

function mergeModels<T>(defaults: T, params?: Partial<T>): T {
  return Object.assign({}, defaults, params);
}
